import React, { Fragment, useState, useEffect, useCallback, useMemo } from "react";
import {
  Combobox,
  ComboboxInput,
  ComboboxButton,
  ComboboxOptions,
  ComboboxOption,
  Transition,
} from "@headlessui/react";
import {
  ChevronDownIcon,
  CheckIcon,
  XIcon,
  RefreshCcwIcon,
} from "lucide-react";
import {
  assignKnowledgeBaseToUser,
  getUsers,
  unassignKnowledgeBaseFromUser,
} from "../../../../service/apiService";
import { User } from "../../../../types";
import { useUser } from "../../../../context/UserContext";

interface AssignUsersProps {
  currentBrainBank?: {
    id: string;
    name: string;
  };
}

const AssignUsers: React.FC<AssignUsersProps> = ({ currentBrainBank }) => {
  const [users, setUsers] = useState<User[]>([]);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [userQuery, setUserQuery] = useState("");
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);
  const [isAssigning, setIsAssigning] = useState(false);
  const [assignSuccess, setAssignSuccess] = useState<boolean | null>(null);
  const [isUnassigning, setIsUnassigning] = useState<string | null>(null);
  const [unassignSuccess, setUnassignSuccess] = useState<boolean | null>(null);
  
  const { userId } = useUser();

  // ✅ Calculate users with access to current BrainBank
  const usersWithAccess = useMemo(() => {
    if (!currentBrainBank || !users.length) return [];
    
    return users.filter(user => 
      user.assigned_knowledge_base?.includes(currentBrainBank.id)
    );
  }, [users, currentBrainBank]);

  // ✅ Memoize the refresh function
  const refreshUsersData = useCallback(async () => {
    setIsLoadingUsers(true);
    try {
      const result = await getUsers();
      setUsers(result);
    } catch (error) {
      console.error("Error fetching users:", error);
    } finally {
      setIsLoadingUsers(false);
    }
  }, []);

  // ✅ Fetch users once on mount
  useEffect(() => {
    refreshUsersData();
  }, [userId, refreshUsersData]);

  // ✅ Filter users based on query
  const filteredUsers = useMemo(() => {
    return userQuery === ""
      ? []
      : users.filter((user) => user.id.toLowerCase() === userQuery.toLowerCase());
  }, [userQuery, users]);

  // ✅ Assign current BrainBank to selected user
  const handleAssign = async () => {
    if (!selectedUser || !currentBrainBank) return;

    setIsAssigning(true);
    setAssignSuccess(null);

    try {
      // Get user's current assignments and add the current BrainBank
      const currentAssignments = selectedUser.assigned_knowledge_base || [];
      
      // Check if already assigned
      if (currentAssignments.includes(currentBrainBank.id)) {
        setAssignSuccess(false);
        console.log("User already has access to this BrainBank");
        return;
      }

      const updatedAssignments = [...currentAssignments, currentBrainBank.id];
      
      await assignKnowledgeBaseToUser(selectedUser.id, updatedAssignments);
      setAssignSuccess(true);
      await refreshUsersData();
      
      // Clear selection after successful assignment
      setSelectedUser(null);
      setUserQuery("");
      
      console.log("BrainBank assigned successfully");
    } catch (error) {
      console.error("Error assigning BrainBank:", error);
      setAssignSuccess(false);
    } finally {
      setIsAssigning(false);
    }
  };

  // ✅ Remove access from current BrainBank
  const handleRemoveAccess = async (userId: string) => {
    if (!currentBrainBank) return;
    
    setIsUnassigning(userId);
    setUnassignSuccess(null);
    
    try {
      await unassignKnowledgeBaseFromUser(userId, [currentBrainBank.id]);
      await refreshUsersData();
      setUnassignSuccess(true);
      console.log("Access removed successfully");
    } catch (error) {
      console.error("Error removing access:", error);
      setUnassignSuccess(false);
    } finally {
      setIsUnassigning(null);
    }
  };

  return (
    <div className="space-y-6">
      {/* Simple Users List - No Colored Box */}
      {currentBrainBank && (
        <div className="mt-4">
          <h3 className="text-sm font-medium text-gray-700">
            Users already assigned to this BrainBank:
          </h3>
          
          {isLoadingUsers ? (
            <div className="mt-2 flex items-center">
              <RefreshCcwIcon className="animate-spin h-4 w-4 text-gray-400 mr-2" />
              <span className="text-sm text-gray-500">Loading users...</span>
            </div>
          ) : usersWithAccess.length > 0 ? (
            <ul className="mt-2">
              {usersWithAccess.map((user) => (
                <li
                  key={user.id}
                  className="flex justify-between items-center text-sm text-gray-700 py-1"
                >
                  {user.id}
                  <button
                    onClick={() => handleRemoveAccess(user.id)}
                    className="text-red-500 hover:text-red-700"
                    title={`Remove ${user.id} access to this BrainBank`}
                    disabled={isUnassigning === user.id}
                  >
                    {isUnassigning === user.id ? (
                      <RefreshCcwIcon className="animate-spin h-5 w-5" />
                    ) : (
                      <XIcon className="h-5 w-5" />
                    )}
                  </button>
                </li>
              ))}
            </ul>
          ) : (
            <p className="mt-2 text-sm text-gray-500">
              No users are currently assigned to this BrainBank.
            </p>
          )}
        </div>
      )}

      {/* Success/Error Messages for removal */}
      {unassignSuccess !== null && (
        <div className="flex items-center">
          {unassignSuccess ? (
            <>
              <CheckIcon className="h-5 w-5 text-green-500" />
              <span className="ml-2 text-sm text-green-500">
                User unassigned successfully
              </span>
            </>
          ) : (
            <>
              <XIcon className="h-5 w-5 text-red-500" />
              <span className="ml-2 text-sm text-red-500">
                Failed to unassign user
              </span>
            </>
          )}
        </div>
      )}

      {/* Divider */}
      <div className="border-t border-gray-200 pt-4">
        <h3 className="text-lg font-medium text-gray-900 mb-4">
          Assign BrainBank to Users
        </h3>
      </div>

      {/* User Selection */}
      <div className="flex flex-col">
        <label htmlFor="user" className="block text-sm font-medium text-gray-700">
          Select User
        </label>
        <Combobox value={selectedUser} onChange={setSelectedUser}>
          <div className="relative mt-1">
            <div className="relative w-full cursor-default overflow-hidden rounded-lg bg-white text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-200 focus-visible:ring-offset-2 sm:text-sm h-12">
              <ComboboxInput
                className="w-full h-full border border-gray-200 py-2 pl-3 pr-10 text-sm leading-5 text-gray-700 bg-white rounded-lg focus:outline-none focus:border-violet-500 focus:ring focus:ring-violet-200 focus:ring-opacity-20"
                displayValue={(user: User) => user?.id ?? ""}
                onChange={(event) => setUserQuery(event.target.value)}
                // No `value` prop: binding the input to userQuery overrides
                // displayValue, and the dropdown's afterLeave clears userQuery
                // on close -- so picking a user emptied the box even though the
                // selection had been made. Letting displayValue drive the input
                // means the chosen user stays visible until the assignment is
                // saved, which clears selectedUser and empties it properly.
              />
              {isLoadingUsers && (
                <div className="absolute inset-y-0 right-0 flex items-center pr-8">
                  <RefreshCcwIcon className="animate-spin h-5 w-5 text-gray-400" />
                </div>
              )}
              <ComboboxButton className="absolute inset-y-0 right-0 flex items-center pr-2">
                <ChevronDownIcon className="h-5 w-5 text-gray-400" />
              </ComboboxButton>
            </div>
            <Transition
              as={Fragment}
              leave="transition ease-in duration-100"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
              afterLeave={() => setUserQuery("")}
            >
              <ComboboxOptions className="absolute mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black/5 focus:outline-none sm:text-sm z-10">
                {filteredUsers.length === 0 ? (
                  <div className="relative cursor-default select-none px-4 py-2 text-gray-700">
                    Please enter the full email address to find and select a user.
                  </div>
                ) : (
                  filteredUsers.map((user) => (
                    <ComboboxOption
                      key={user.id}
                      className={({ active }) =>
                        `relative cursor-default select-none py-2 pl-10 pr-4 ${
                          active ? "bg-violet-600 text-white" : "text-gray-900"
                        }`
                      }
                      value={user}
                    >
                      {({ selected, active }) => (
                        <>
                          <span className={`block truncate ${selected ? "font-medium" : "font-normal"}`}>
                            {user.id}
                          </span>
                          {selected ? (
                            <span className={`absolute inset-y-0 left-0 flex items-center pl-3 ${active ? "text-white" : "text-violet-600"}`}>
                              <CheckIcon className="h-5 w-5" />
                            </span>
                          ) : null}
                        </>
                      )}
                    </ComboboxOption>
                  ))
                )}
              </ComboboxOptions>
            </Transition>
          </div>
        </Combobox>
      </div>

      {/* Original Button Styling and Placement */}
      {selectedUser && (
        <>
          <div className="flex flex-col mt-4 relative">
            {/* Original Button - Exact Same Styling */}
            <button
              onClick={handleAssign}
              className={`inline-flex justify-center items-center py-2 px-4 w-32 mt-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white ${
                isAssigning || !selectedUser || !currentBrainBank
                  ? "bg-gray-400 cursor-not-allowed"
                  : "bg-violet-600 hover:bg-violet-700"
              } focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-violet-500`}
              disabled={isAssigning || !selectedUser || !currentBrainBank}
            >
              {isAssigning ? (
                <div className="flex items-center justify-center">
                  Assigning
                  <RefreshCcwIcon className="ml-2 animate-spin h-5 w-5 text-white" />
                </div>
              ) : (
                "Assign"
              )}
            </button>
          </div>

          {/* Original Assignment Success/Error Messages */}
          {assignSuccess !== null && (
            <div className="mt-4 flex items-center">
              {assignSuccess ? (
                <>
                  <CheckIcon className="h-6 w-6 text-green-500" />
                  <span className="ml-2 text-sm text-green-500">
                    BrainBank assigned successfully
                  </span>
                </>
              ) : (
                <>
                  <XIcon className="h-6 w-6 text-red-500" />
                  <span className="ml-2 text-sm text-red-500">
                    {selectedUser?.assigned_knowledge_base?.includes(currentBrainBank?.id || '') 
                      ? "User already has access to this BrainBank" 
                      : "Failed to assign BrainBank"}
                  </span>
                </>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default AssignUsers;